<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import {
  listPendingTribes,
  approveTribe,
  rejectTribe,
  imageUrl,
  type TribePendingItem,
} from '../api';

const loading = ref(false);
const list = ref<TribePendingItem[]>([]);
const submitting = ref(false);

function normalize(e: unknown): string {
  const err = e as { message?: string };
  return err.message ?? '操作失败';
}

async function reload() {
  loading.value = true;
  try {
    const r = await listPendingTribes();
    list.value = r.items;
  } catch (e) {
    ElMessage.error(normalize(e));
  } finally {
    loading.value = false;
  }
}

async function onApprove(item: TribePendingItem) {
  submitting.value = true;
  try {
    await approveTribe(item.id);
    ElMessage.success('已通过');
    await reload();
  } catch (e) {
    ElMessage.error(normalize(e));
  } finally {
    submitting.value = false;
  }
}

async function onReject(item: TribePendingItem) {
  try {
    const { value } = await ElMessageBox.prompt('请输入驳回原因', '驳回部落', {
      confirmButtonText: '确认驳回',
      cancelButtonText: '取消',
      inputPattern: /\S{2,}/,
      inputErrorMessage: '原因至少 2 字',
    });
    submitting.value = true;
    await rejectTribe(item.id, value);
    ElMessage.success('已驳回');
    await reload();
  } catch (e) {
    if (e !== 'cancel') ElMessage.error(normalize(e));
  } finally {
    submitting.value = false;
  }
}

onMounted(reload);
</script>

<template>
  <div>
    <div class="bar">
      <span class="muted">待审部落 {{ list.length }} 个</span>
      <el-button size="small" @click="reload" :loading="loading">刷新</el-button>
    </div>

    <el-table :data="list" v-loading="loading" style="width: 100%">
      <el-table-column label="封面" width="90">
        <template #default="{ row }">
          <el-image
            v-if="row.coverUrl"
            :src="imageUrl(row.coverUrl)"
            fit="cover"
            style="width: 60px; height: 60px; border-radius: 6px"
          />
          <span v-else class="muted">无</span>
        </template>
      </el-table-column>
      <el-table-column prop="name" label="部落名" width="160" />
      <el-table-column prop="intro" label="简介" show-overflow-tooltip />
      <el-table-column label="创建者" width="180">
        <template #default="{ row }">
          <div>{{ row.creatorName || row.creatorId }}</div>
          <div class="muted">{{ row.creatorSchool || '—' }}</div>
        </template>
      </el-table-column>
      <el-table-column prop="createdAt" label="申请时间" width="180" />
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="{ row }">
          <el-button size="small" type="success" :disabled="submitting" @click="onApprove(row)">
            通过
          </el-button>
          <el-button size="small" type="danger" :disabled="submitting" @click="onReject(row)">
            驳回
          </el-button>
        </template>
      </el-table-column>
      <template #empty>暂无待审部落</template>
    </el-table>
  </div>
</template>

<style scoped>
.bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.muted {
  color: #999;
  font-size: 13px;
}
</style>
